import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/Input';

export interface TableToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  /** Debounce delay in ms before onSearchChange fires. */
  debounceMs?: number;
  /** Filter controls, rendered between the search field and the actions slot. */
  children?: ReactNode;
  /** Right-aligned actions (e.g. Export). */
  actions?: ReactNode;
}

/**
 * Table toolbar shell: debounced search on the left, filter controls in the
 * middle, actions on the right.
 *
 * Debounced at the DOM-value level (a local `draft` state, not the `search`
 * prop) so the field stays responsive to every keystroke while
 * `onSearchChange` (and the network request it triggers) only fires once
 * typing settles.
 */
export function TableToolbar({
  search,
  onSearchChange,
  debounceMs = 300,
  children,
  actions,
}: TableToolbarProps) {
  const [draft, setDraft] = useState(search);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => setDraft(search), [search]);

  function handleChange(value: string) {
    setDraft(value);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => onSearchChange(value), debounceMs);
  }

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="min-w-56">
        <Input
          label="Search"
          icon={<Search size={16} aria-hidden />}
          placeholder="Search..."
          value={draft}
          onChange={(e) => handleChange(e.target.value)}
        />
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  );
}
